import React, { useMemo } from "react";

export default function Dashboard({ submissions = [], loading = false, fetchError = null, fetchData }) {
  const toNumber = (val) => {
    if (typeof val === "number" && Number.isFinite(val)) return val;
    if (typeof val === "string") {
      const n = Number(val.replace(/[$,\s]/g, ""));
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const getFund = (sub) => {
    const d = sub?.data || {};
    return d["fundName"] || "Placeholder for fund name";
  };

  const getAmount = (sub) => {
    const d = sub?.data || {};
    const val = d["amount"] || d["formattedAmount"] || "0";
    return toNumber(val);
  };

  const fundData = useMemo(() => {
    const funds = new Map();

    for (const sub of submissions) {
      const fund = String(getFund(sub));
      const amt = getAmount(sub);
      const d = sub?.data || {};

      if (!funds.has(fund)) {
        funds.set(fund, {
          totalCapital: 0,
          investorCount: 0,
          stateBreakdown: {},
          qualifiedClients: 0,
          nonQualifiedClients: 0,
          qualifiedPurchasers: 0,
          nonQualifiedPurchasers: 0,
          erisaCapital: 0,
        });
      }

      const f = funds.get(fund);
      f.totalCapital += amt;
      f.investorCount += 1;

      // state breakdown (look at both domicileSelectedState and entityMiscMattersFund.domicile)
      const state =
        d["domicileSelectedState"] ||
        d?.entityMiscMattersFund?.domicile ||
        d?.entityMiscMatters?.domicile ||
        d["state"] ||
        "Unknown";
      if (!f.stateBreakdown[state]) {
        f.stateBreakdown[state] = { count: 0, capital: 0, earliestDate: null };
      }
      f.stateBreakdown[state].count += 1;
      f.stateBreakdown[state].capital += amt;
      const created = new Date(sub.createdAt);
      if (!f.stateBreakdown[state].earliestDate || created < f.stateBreakdown[state].earliestDate) {
        f.stateBreakdown[state].earliestDate = created;
      }

      // qualifications
      if (
        d?.entityInvestmentDetails?.qualifiedClientNetWorth ||
        d?.entityInvestmentDetails?.lookThroughEntityOwnerNetWorth ||
        d?.entityInvestmentDetails?.lookThroughIndirectOwnerNetWorth
      ) {
        f.qualifiedClients += 1;
      } else {
        f.nonQualifiedClients += 1;
      }

      if (
        d?.entityInvestmentAdvisers?.trustQualifiedPurchaser ||
        d?.entityInvestmentAdvisers?.companyQualifiedPurchaser
      ) {
        f.qualifiedPurchasers += 1;
      } else {
        f.nonQualifiedPurchasers += 1;
      }

      // ERISA (look at both individual miscMatters and entityMiscMattersFund)
      const erisaFlags = [
        d?.miscMatters?.subject4975,
        d?.entityAccreditedInvestor?.erisaFiduciary,
        d?.entityAccreditedInvestor?.erisaAssets,
        d?.entityAccreditedInvestor?.erisaSelfDirected,
        d?.entityMiscMattersFund?.erisaPartner,
      ];
      if (erisaFlags.some((v) => v === true || v === "true")) {
        f.erisaCapital += amt;
      }
    }

    return Array.from(funds.entries()).map(([fund, stats]) => ({ fund, ...stats }));
  }, [submissions]);

  const fmtUSD = (n) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

  if (loading) return <div>Loading submissions…</div>;
  if (fetchError) return <div style={{ color: "red" }}>{fetchError}</div>;
  if (!submissions.length)
    return (
      <div>
        <h2>Dashboard</h2>
        <p>No submissions yet.</p>
        {fetchData && (
          <button onClick={fetchData} style={styles.button}>Refresh</button>
        )}
      </div>
    );

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <h2 style={{ margin: 0 }}>Fund Dashboard</h2>
        {fetchData && (
          <button onClick={fetchData} style={styles.button}>Refresh</button>
        )}
      </div>

      {fundData.map((fund) => {
        const erisaPct = fund.totalCapital ? ((fund.erisaCapital / fund.totalCapital) * 100).toFixed(1) : "0.0";

        return (
          <div key={fund.fund} style={styles.fundPanel}>
            <h3>{fund.fund}</h3>
            <div style={styles.cardsRow}>
              <StatCard label="Total Committed Capital" value={fmtUSD(fund.totalCapital)} />
              <StatCard label="Number of Investors" value={String(fund.investorCount)} />
              <StatCard label="ERISA Capital" value={`${fmtUSD(fund.erisaCapital)} (${erisaPct}%)`} />
            </div>

            <div style={styles.grid}>
              <div style={styles.panel}>
                <h4 style={styles.h3}>State Breakdown</h4>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>State</th>
                      <th style={styles.th}># Investors</th>
                      <th style={styles.th}>Capital</th>
                      <th style={styles.th}>First Countersign</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(fund.stateBreakdown).map(([state, info]) => (
                      <tr key={state}>
                        <td style={styles.td}>{state}</td>
                        <td style={styles.td}>{info.count}</td>
                        <td style={styles.td}>{fmtUSD(info.capital)}</td>
                        <td style={styles.td}>{info.earliestDate ? info.earliestDate.toLocaleDateString() : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={styles.panel}>
                <h4 style={styles.h3}>Investor Qualifications</h4>
                <p>Qualified Clients: {fund.qualifiedClients} vs Non-Qualified: {fund.nonQualifiedClients}</p>
                <p>Qualified Purchasers: {fund.qualifiedPurchasers} vs Non-Qualified: {fund.nonQualifiedPurchasers}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardValue}>{value}</div>
      <div style={styles.cardLabel}>{label}</div>
    </div>
  );
}

const styles = {
  wrap: { padding: 20 },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  cardsRow: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 },
  card: { flex: "1 1 150px", padding: 16, border: "1px solid #ddd", borderRadius: 8, background: "#fafafa" },
  cardValue: { fontSize: 20, fontWeight: "bold" },
  cardLabel: { fontSize: 12, color: "#666" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 },
  panel: { border: "1px solid #ddd", borderRadius: 8, padding: 16, background: "#fff" },
  h3: { marginTop: 0 },
  th: { borderBottom: "1px solid #ccc", textAlign: "left", padding: 4 },
  td: { borderBottom: "1px solid #eee", padding: 4 },
  fundPanel: { marginBottom: 40 },
  button: { padding: "6px 12px", borderRadius: 4, cursor: "pointer" },
};
